import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CalendarIcon, UserIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import PhotoUpload from './PhotoUpload';
import EditableTitle from './EditableTitle';
import EditableDescription from './EditableDescription';

export const StoryHeader = ({ story }) => {
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [savingCover, setSavingCover] = useState(false);

  // Load existing cover photo from story data
  useEffect(() => {
    if (story?.coverPhoto) {
      setCoverPhoto(story.coverPhoto);
    }
  }, [story]);

  const handleCoverPhotoUpdate = async (photos) => {
    if (!story?.id) return;
    
    setSavingCover(true);
    try {
      // Use the first photo as the cover photo
      const newCoverPhoto = photos.length > 0 ? photos[0] : null;
      
      // Update the story document with the cover photo
      await updateDoc(doc(db, 'stories', story.id), {
        coverPhoto: newCoverPhoto,
        updatedAt: serverTimestamp()
      });
      
      setCoverPhoto(newCoverPhoto);
      setShowPhotoUpload(false);
      
    } catch (error) {
      console.error('Error saving cover photo:', error);
      alert('Failed to save cover photo. Please try again.');
    } finally {
      setSavingCover(false);
    }
  };

  const handleRemoveCoverPhoto = async () => {
    if (!story?.id) return;
    
    setSavingCover(true);
    try {
      await updateDoc(doc(db, 'stories', story.id), {
        coverPhoto: null,
        updatedAt: serverTimestamp()
      });
      
      setCoverPhoto(null);
      
    } catch (error) {
      console.error('Error removing cover photo:', error);
      alert('Failed to remove cover photo. Please try again.');
    } finally {
      setSavingCover(false);
    }
  };

  return (
    <div className="memory-stream-card">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        {/* Cover Photo Section - Hidden for demo stories */}
        {!story?.isDemo && (
        <div className="flex-shrink-0 w-full md:w-auto">
          {coverPhoto ? (
            <div className="relative group w-full md:w-32">
              <img
                src={coverPhoto.url}
                alt="Story cover"
                className="w-full md:w-32 h-48 md:h-32 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
              />
              <button
                onClick={handleRemoveCoverPhoto}
                disabled={savingCover}
                className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 disabled:opacity-50"
                title="Remove cover photo"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="w-full md:w-32 h-48 md:h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
              <button
                onClick={() => setShowPhotoUpload(true)}
                className="flex flex-col items-center text-gray-500 hover:text-gray-700 p-4 text-center"
              >
                <PhotoIcon className="h-8 w-8 mb-2" />
                <span className="text-xs font-medium">Add Cover Photo</span>
              </button>
            </div>
          )}
          
          {/* Cover Photo label under the image */}
          <p className="text-sm text-gray-600 mt-2 text-center">Cover Photo</p>
        </div>
        )}

        {/* Story Content */}
        <div className="flex-1 min-w-0 w-full">
          <EditableTitle story={story} />
          <EditableDescription story={story} />

          <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-2 md:space-y-0 text-sm text-gray-500">
            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 mr-1" />
              Created {story.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
            </div>
            <div className="flex items-center">
              <UserIcon className="h-4 w-4 mr-1" />
              Personal Story
            </div>
          </div>
        </div>
      </div>

      {savingCover && (
        <div className="mt-4 text-center text-sm text-gray-500">
          Saving cover photo...
        </div>
      )}

      {/* Photo Upload Modal/Popup */}
      {showPhotoUpload && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 999999, position: 'fixed', isolation: 'isolate', WebkitTransform: 'translateZ(0)' }}
        >
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Cover Photo</h3>
              <button
                onClick={() => setShowPhotoUpload(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4">
              <PhotoUpload
                onPhotosUpdate={handleCoverPhotoUpdate}
                existingPhotos={[]}
                storyId={story?.id}
                storagePrefix="cover"
              />
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowPhotoUpload(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
